/**
 * Email Service Types
 */

import type { OtpDeliveryAttempt } from "@/lib/auth/otp-delivery-types";

export interface EmailRecipient {
  email: string;
  name?: string;
}

export type EmailDeliveryStatus = "requested" | "accepted" | "delayed" | "failed" | "unknown";

/** Safe delivery diagnostics; never contains message bodies or authentication links. */
export interface EmailDeliveryMessage {
  messageId: string;
  recipient: string;
  subject: string;
  sentAt: string;
  status: EmailDeliveryStatus;
  reason: string;
  events: Array<{ type: string; at: string; details: string }>;
}

export interface EmailDeliveryHistory {
  messages: EmailDeliveryMessage[];
  unavailable: boolean;
}

/**
 * Admin detail pages that show a contact's delivery history — the OTP entity
 * vocabulary, because the service matches it against `otp_entity_type`.
 */
export type EmailDeliveryEntityType = OtpDeliveryAttempt["entityType"];

export type TrackLinksOption = "None" | "HtmlAndText" | "HtmlOnly" | "TextOnly";

export interface EmailMessage {
  to: EmailRecipient;
  from?: EmailRecipient;
  /** Explicit reply destination; omitted to retain the sender's existing routing. */
  replyTo?: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  tag?: string;
  metadata?: Record<string, string>;
  // Postmark tracking options (for broadcast/campaign emails)
  stream?: string; // Message stream: "outbound" (default) or "broadcast"
  trackOpens?: boolean; // Enable open tracking
  trackLinks?: TrackLinksOption; // Enable click tracking
}

export interface EmailSendResult {
  success: boolean;
  messageId?: string;
  errorCode?: string;
  errorMessage?: string;
  /**
   * True when the transport deliberately did NOT call Postmark because the
   * recipient is not a real address (a `@phone.local` placeholder) or is outside
   * the non-production `EMAIL_RECIPIENT_ALLOWLIST`. Reported as
   * `success: true` so callers treat it as terminal (no retry, no dead-letter),
   * exactly like a suppressed recipient.
   */
  skipped?: boolean;
  /**
   * Set only when the provider refused the whole request (non-2xx). `errorCode` then
   * carries either the body's Postmark `ErrorCode` or, absent one, this status — two
   * vocabularies in one string. Callers that must tell "conclusively rejected" from
   * "unknown, possibly partially accepted" read this field, never `Number(errorCode)`.
   */
  httpStatus?: number;
}

export interface BatchEmailResult {
  sent: number;
  failed: number;
  /** Recipients intentionally not sent to; excluded from sent and failed. */
  skipped: number;
  results: Array<EmailSendResult & { email: string; metadata?: Record<string, string> }>;
}

// Notification preferences
export interface NotificationSettings {
  email_match_alerts: boolean;
  email_message_alerts: boolean;
  email_deal_updates: boolean;
}

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  email_match_alerts: true,
  email_message_alerts: true,
  email_deal_updates: true,
};

// ============================================================================
// Offering/Request Notification Data Types
// ============================================================================

export type OfferingType =
  | "okopunkte"
  | "ausgleichsflaechen"
  | "co2_zertifikate"
  | "jagdpachten"
  | "landverpachtung";

export interface OfferingNotificationData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
  userPhone: string | null;
  offeringId: string;
  offeringTitle: string;
  /** What the Angebot offers in words, as `describeSupplies` names its rows (ARCH-039 D19). */
  offeringSupplies: string;
  /** The axes it offers on, for a subject line ("Naturschutz, Forst"); null while it offers none. */
  offeringAxes: string | null;
  offeringLocation: string | null;
  offeringNaturraum: string | null;
}

export interface RequestNotificationData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
  userPhone: string | null;
  requestId: string;
  requestTitle: string;
  /** The request's needs in words, as `describeNeeds` names them (every need is a line, ARCH-039 D19). */
  requestNeeds: string;
  /** The axes the request needs, for a subject line ("Naturschutz, Forst"); null while it needs none. */
  requestAxes: string | null;
  requestLocation: string | null;
  requestNaturraum: string | null;
  requestOrganization: string | null;
  /** Optional: the preview offering the buyer clicked in the funnel, preformatted. */
  requestInterestedOffering?: string | null;
}

/**
 * Shared seller/buyer/offering/request detail for the admin match alerts. Both
 * the auto-created "new match/deal" alert and the (Phase 2, currently dormant)
 * "deal possible" alert carry this block; each adds its own identifiers.
 */
export interface AdminMatchPartyData {
  /**
   * The axis of the supply the alert is about (a deal's row, a V1 detection's Biotopausgleich),
   * which names the alert when no matched line names the traded one; null without such a row.
   */
  offeringAxes: string | null;
  // Seller info
  sellerId: string;
  sellerName: string;
  sellerEmail: string;
  sellerPhone: string | null;
  sellerLocation: string | null;
  sellerNaturraum: string | null;
  sellerBundesland: string | null;
  sellerPLZ: string | null;
  sellerPricePerUnit: string | null;
  offeringId: string;
  offeringTitle: string;
  offeringQuantity: string | null;
  // Buyer info
  buyerId: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string | null;
  buyerLocation: string | null;
  buyerNaturraum: string | null;
  buyerBundesland: string | null;
  buyerPLZ: string | null;
  buyerPricePerUnit: string | null;
  requestId: string;
  requestTitle: string;
  requestQuantity: string | null;
}

export interface AdminMatchAlertData extends AdminMatchPartyData {
  matchId: string;
  /** Line grain (ARCH-039): the traded axis ("Artenschutz") — replaces the product type label. */
  matchedAxisLabel?: string;
  // Deal info
  dealId: string;
  dealSum: string | null;
  /** An internal deal is a hold its parties must not hear of: the alert says so. */
  visibility: "internal" | "shared";
}

/**
 * Admin "deal possible" alert — the matching engine detected a viable
 * offering×request pairing but did NOT create a deal (matching is detection-only;
 * deal-making is manual). Same party detail as AdminMatchAlertData, but no
 * `dealId`/`dealSum` (no deal exists yet). The CTA links to the request's
 * Details, where an admin creates the deal manually from a row's Treffer.
 */
export interface AdminDealPossibleData extends AdminMatchPartyData {
  matchedAxisLabel?: string;
  opportunityId: string;
}

export interface RegistrationWelcomeData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
}

/**
 * Payload for `security-email-claim-attempt` — sent to the REAL account
 * owner when someone else submits a funnel form using their email.
 * Must NOT include submitter identifiers or any data that would leak
 * attacker information to the owner beyond what they typed.
 */
export interface SecurityEmailClaimAttemptData {
  /** Account owner's user id (recipient). */
  userId: string;
  /** Account owner's email (recipient address). */
  email: string;
  /** Account owner's display name, may be null for accounts without a name set. */
  userName: string | null;
  /** Pre-rendered German salutation, e.g. "Sehr geehrte Frau Müller". */
  userSalutation: string;
  /**
   * The email address that was used to submit the form. Almost always
   * equals the owner's own email; we still pass it so the template can
   * echo it back for transparency.
   */
  claimedEmail: string;
  /** Human-readable German timestamp of the claim attempt. */
  attemptTimestamp: string;
  /** Absolute login URL. */
  loginUrl: string;
  /** Support contact email for follow-up questions. */
  supportEmail: string;
}

export interface AdminMessageData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
  senderName: string;
  messagePreview: string;
  dealId: string;
  offeringId: string;
  requestId: string | null;
  direction: "buyer" | "seller";
}

export interface UserMessageAdminData {
  dealId: string;
  userName: string;
  userEmail: string;
  messagePreview: string;
}

// Note: DataCheckData is no longer used - campaigns now use lib/campaigns/types
// Kept for backwards compatibility with any remaining code that imports it
export interface DataCheckData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
  userRole: string; // "Verkäufer" | "Käufer" | "Verkäufer/Käufer"
  campaignId: string;
}

// ============================================================================
// Deal Started Notification Data
// ============================================================================

export interface DealStartedNotificationData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
  dealId: string;
  offeringId: string;
  offeringTitle: string;
  offeringLocation: string | null;
  /** Request title (buyer's perspective) */
  requestTitle: string | null;
  requestId: string | null;
  // Direction: who is being notified
  direction: "buyer" | "seller";
}

// ============================================================================
// Deal Activity Admin Notification Data
// ============================================================================

export interface DealActivityAdminData {
  dealId: string;
  actorName: string;
  actorEmail: string;
  actorRole: "seller" | "buyer";
  actionLabel: string;
  offeringTitle: string;
  dealUrl: string;
}

// ============================================================================
// Deal Deadline Lapsed Admin Notification Data (ARCH-026 §Deadlines)
// ============================================================================

/**
 * A deal passed its deadline without reaching signature. There is no actor —
 * the ABSENCE of action is the event — which is why this does not reuse
 * `DealActivityAdminData` and its person-shaped fields.
 *
 * Every field arrives display-ready; the MJML template does no formatting and
 * no pluralisation.
 */
export interface DealDeadlineLapsedAdminData {
  dealId: string;
  dealTitle: string;
  /** German date, e.g. "17. Juli 2026". */
  deadlineDate: string;
  /** Humanised span, e.g. "3 Tagen". */
  daysOverdue: string;
  statusLabel: string;
  sellerLabel: string;
  buyerLabel: string;
  dealUrl: string;
}

// ============================================================================
// Deal Completed Admin Notification Data
// ============================================================================

export interface DealCompletedAdminData {
  dealId: string;
  sellerName: string;
  buyerName: string;
  offeringTitle: string;
  finalQuantity: string | null;
  finalPrice: string | null;
  dealUrl: string;
}

// ============================================================================
// Deal Status Update Notification Data
// ============================================================================

export interface DealStatusUpdateNotificationData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
  dealId: string;
  offeringId: string;
  requestId: string | null;
  itemTitle: string;
  stepHeadline: string;
  stepDescription: string;
  direction: "buyer" | "seller";
}

// ============================================================================
// Deal Completed User Notification Data
// ============================================================================

export interface DealCompletedUserNotificationData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
  dealId: string;
  offeringId: string;
  requestId: string | null;
  itemTitle: string;
  direction: "buyer" | "seller";
}

// ============================================================================
// Deal Notes Updated Notification Data
// ============================================================================

export interface DealNotesUpdatedNotificationData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
  dealId: string;
  offeringId: string;
  requestId: string | null;
  itemTitle: string;
  notes: string;
  direction: "buyer" | "seller";
}

// ============================================================================
// Invoice Notification Data
// ============================================================================

export interface InvoiceNotificationData {
  userId: string;
  email: string;
  userName: string | null;
  userSalutation: string;
  dealId: string;
  offeringId: string;
  requestId: string | null;
  itemTitle: string;
  invoiceNumber: string;
  invoiceTotal: string;
}

// ============================================================================
// Account Deletion Notification Data
// ============================================================================

export interface AccountDeletedUserData {
  email: string;
  userName: string | null;
  userSalutation: string;
  cancelledOfferingsCount: number;
  cancelledRequestsCount: number;
}

export interface AccountDeletedAdminData {
  userName: string;
  userEmail: string;
  source: string;
  cancelledOfferingsCount: number;
  cancelledRequestsCount: number;
}

// ============================================================================
// Contact Notification Data
// ============================================================================

export interface ContactSubmittedNotificationData {
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  topic: string;
  message: string | null;
}

// ============================================================================
// OPaaS Notification Data
// ============================================================================

export interface OPaaSInquiryNotificationData {
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  message: string | null;
}

export interface OPaaSUserInvitedNotificationData {
  organizationName: string;
  invitedEmail: string;
  role: string;
  invitedBy: string;
  statusNote: string;
}

export interface OPaaSTenantUpdatedNotificationData {
  organizationName: string;
  changedBy: string;
  changedFields: string[];
}

export interface OPaaSProjectImportedNotificationData {
  organizationName: string;
  importedCount: number;
  importedBy: string;
}

export interface OPaaSProjectCreatedNotificationData {
  organizationName: string;
  projectName: string;
  createdBy: string;
}

export interface OPaaSInviteAcceptedNotificationData {
  organizationName: string;
  acceptedByEmail: string;
  acceptedByName: string;
  role: string;
  recipients: Array<{ email: string; name: string }>;
}

export interface OPaaSMemberDeactivatedNotificationData {
  organizationName: string;
  deactivatedEmail: string;
  role: string;
  deactivatedBy: string;
  recipients: Array<{ email: string; name: string }>;
}

// ============================================================================
// Admin Comment Mention Notification Data
// ============================================================================

export interface AdminCommentMentionData {
  /** Recipient (the mentioned admin). */
  recipientEmail: string;
  recipientName: string | null;
  /** Author of the comment. */
  authorName: string;
  /** "Anfrage" | "Angebot" | "Flächenprüfung" */
  entityTypeLabel: string;
  /** Raw comment body (escaped + newline-converted by the sender). */
  commentBody: string;
  /** Deep link into the admin detail page for the entity. */
  entityAdminUrl: string;
}
